import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { TImportDataDictionary, TObj } from "components/ImportData/type";
import type { TFileData } from "containers/BulkUpdatesContainer";
import type { TPendingImport } from "containers/BulkUpdatesContainer/ConfirmationStep";
import ExcelJS from "exceljs";
import { payloadTooLargeDetailsFromError } from "helpers/jsonPayloadLimit";
import { getMultipartChunks, multipartUploadToS3 } from "helpers/s3";
import { downloadFile, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { ImportIndex, TImportTargets } from "types";

import { fetchActiveJobs } from "./jobsStore";
import type { TFetchStatus, TStock } from "./types";

const getRejectPayload = (error: unknown) => {
    const tooLarge = payloadTooLargeDetailsFromError(error);
    if (tooLarge) {
        return {
            error: "ServerError.PayloadTooLarge",
            message: "ImportData.PayloadTooLarge",
            payloadBytes: tooLarge.payloadBytes,
            limitBytes: tooLarge.limitBytes,
        };
    }

    const responseData = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
    return responseData ?? { message: "ImportData.FileError" };
};

export const getImportErrorsFromPayload = (payload: Record<string, unknown> | undefined): Record<string, unknown>[] => {
    const errors = payload?.error ?? payload?.data;
    return Array.isArray(errors) ? errors : [];
};

const applyFailedImportFromPayload = (
    state: {
        importDataStatus: TFetchStatus;
        importErrors: Record<string, unknown>[];
        importErrorMessage: string;
        importPayloadLimit: { payloadBytes: number; limitBytes: number } | null;
    },
    payload: Record<string, unknown> | undefined,
) => {
    const payloadBytes = typeof payload?.payloadBytes === "number" ? payload.payloadBytes : undefined;
    const limitBytes = typeof payload?.limitBytes === "number" ? payload.limitBytes : undefined;
    if (payload?.error === "ServerError.PayloadTooLarge" && payloadBytes != null && limitBytes != null) {
        state.importDataStatus = "failed";
        state.importErrors = [];
        state.importErrorMessage = "ImportData.PayloadTooLarge";
        state.importPayloadLimit = { payloadBytes, limitBytes };
        return;
    }

    const errors = getImportErrorsFromPayload(payload);
    state.importDataStatus = "failed";
    state.importErrors = errors;
    state.importErrorMessage = errors.length ? "" : typeof payload?.message === "string" ? payload.message : "";
    state.importPayloadLimit = null;
};

export const fetchImportBeneficiars = createAsyncThunk(
    "importData/postBulkBeneficiaries",
    async ({ data, filename }: { data: TObj[]; filename: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/beneficiary", {
                    data: data,
                    filename: filename,
                }),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data.result;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

export const fetchImportTitles = createAsyncThunk(
    "importData/postBulkTitles",
    async ({ data, filename }: { data: TObj[]; filename: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/title", {
                    data: data,
                    filename: filename,
                }),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data.result;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

const getUpdatesEndpoint = {
    beneficiars: "/beneficiary",
    titles: "/title",
    terms: "/term",
};

export const bulkUpdates = createAsyncThunk(
    "importData/bulkUpdates",
    async (
        { data, filename, element }: { data: TFileData; filename: string; element: TImportDataDictionary },
        { rejectWithValue },
    ) => {
        try {
            const response = await toast.promise(
                axios.put(import.meta.env.REACT_APP_API_ENDPOINT + getUpdatesEndpoint[element], {
                    data,
                    filename,
                }),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data.result;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

export const verifyImportTitles = createAsyncThunk(
    "importData/verifyImportTitles",
    async ({ data }: { data: TObj[] }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/title/import/verify", {
                data: data,
            });
            return response.data.result;
        } catch (error) {
            return rejectWithValue(getRejectPayload(error));
        }
    },
);

export const fetchImportPublicationsWithArticles = createAsyncThunk(
    "importData/postBulkPublicationsWithArticles",
    async ({ data, filename }: { data: TObj[]; filename: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/title/import-with-articles", { data, filename }),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data.data;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

export const fetchImportArticles = createAsyncThunk(
    "importData/postBulkArticles",
    async ({ titleId, data, filename }: { titleId: number; data: TObj[]; filename: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.post(`${import.meta.env.REACT_APP_API_ENDPOINT}/title/${titleId}/article/import`, {
                    data: data,
                    filename: filename,
                }),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

export const verifyImportTerms = createAsyncThunk(
    "importData/verifyImportTerms",
    async ({ data, periodTypeId }: { data: TObj[]; periodTypeId?: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/term/import/verify", {
                data,
                periodTypeId,
            });
            return response.data.result;
        } catch (error) {
            return rejectWithValue(getRejectPayload(error));
        }
    },
);

export const fetchImportTerms = createAsyncThunk(
    "importData/postBulkTerms",
    async (
        { data, filename, periodTypeId }: { data: TObj[]; filename: string; periodTypeId?: number },
        { rejectWithValue },
    ) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/term/import", {
                    data,
                    filename,
                    periodTypeId,
                }),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data.result;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

type TSalesImport = {
    date: string[];
    periodId: number;
    channelId: number | undefined; // undefined if the channel is the 'allChannels' option
    sales: TObj[];
};
export const fetchImportSales = createAsyncThunk(
    "importData/postBulkSales",
    async ({ data, filename }: { data: TSalesImport; filename: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/sales/import", {
                    data: data,
                    filename: filename,
                }),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );

            return response.data.data;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

export const verifyImportSales = createAsyncThunk(
    "importData/verifyImportSales",
    async ({ data, filename }: { data: TSalesImport; filename: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/sales/import/verify", {
                data,
                filename,
            });
            return response.data.result;
        } catch (error) {
            return rejectWithValue(getRejectPayload(error));
        }
    },
);

export const fetchImportStock = createAsyncThunk(
    "importData/postBulkStock",
    async (
        data: {
            locationId: number | string;
            stocks: Partial<TStock>[];
            filename: string;
            periodId?: number;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/stock/import", data),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data.result;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);
export const fetchImportWorks = createAsyncThunk(
    "importData/postBulkWorks",
    async ({ data, filename }: { data: TObj[]; filename: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/work/import", {
                    data,
                    filename,
                }),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data.result;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

export const fetchImportAdvancePayments = createAsyncThunk(
    "importData/postBulkAdvancePayments",
    async (data: { data: TObj[]; filename: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/advance/import", data),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data.result;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

export const fetchImportProvision = createAsyncThunk(
    "importData/postBulkProvision",
    async (
        data: {
            data: Partial<{ ean13: string; provision: number; reinstatedProvision?: number }>[];
            filename: string;
            periodId?: number;
            target?: "titles" | "terms";
        },
        { rejectWithValue },
    ) => {
        try {
            const endpoint = data.target === "terms" ? "/term/provision/import" : "/title/provision/import";

            const response = await toast.promise(axios.post(import.meta.env.REACT_APP_API_ENDPOINT + endpoint, data), {
                pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                success: `${t("ImportData.SuccessToImportData")}`,
                error: `${t("ImportData.FailedToImportData")}`,
            });
            return response.data.result;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

/**
 * Creates a signed url to upload file to the bucket
 */
export const getSignedURL = createAsyncThunk(
    "importData/getSignedURL",
    async (
        { method, importType }: { method: "create" | "update"; importType: TImportTargets },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + "/history/imports", null, {
                params: { method, importType },
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

// Uploads a file to S3 using multipart upload (6MB chunks)
export const multipartUpload = createAsyncThunk<
    { processingJobId?: number; fileId: string; fileKey: string; bucket: string },
    { data: File | object; method: "create" | "update"; importType: TImportTargets; filename: string }
>("importData/multipartUpload", async ({ data, method, importType, filename }, { rejectWithValue, dispatch }) => {
    try {
        // Get the MIME type and size of the file or object
        // If data is a File, use its properties; otherwise, treat it as JSON
        const contentType = data instanceof File ? data.type : "application/json";
        const fileSize = data instanceof File ? data.size : JSON.stringify(data).length;

        const chunkSizeInMB = 6;
        const chunkSize = chunkSizeInMB * 1024 * 1024; // Convert MB to bytes

        const numOfParts = Math.ceil(fileSize / chunkSize);

        // Get signed URLs for each part/chunk (backend creates job for sales at this point)
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/history/imports/multipart", {
            params: { method, importType, contentType, numOfParts, filename },
        });

        const {
            data: { signedUrls, fileId, fileKey, processingJobId },
        } = response.data as {
            data: {
                signedUrls: string[];
                fileId: string;
                fileKey: string;
                processingJobId?: number;
            };
        };

        if (!signedUrls) {
            throw new Error("No signed URLs returned");
        }

        // Refresh jobs so addSalesButtonStatus finds the new job immediately
        if (processingJobId !== undefined) {
            await dispatch(fetchActiveJobs());
        }

        // Split the data into parts and assign each chunk to a signed URL
        const dataChunks = getMultipartChunks({
            data,
            chunkSize,
            getSignedUrl: (index: number) => {
                return signedUrls[index];
            },
        });

        // Upload each part to S3
        const uploadedParts = await multipartUploadToS3({
            chunks: dataChunks,
        });

        // Finalize the multipart upload (makes the file available in S3)
        const completedMultipartsUploadResponse = await axios.post(
            import.meta.env.REACT_APP_API_ENDPOINT + "/history/imports/multipart",
            {
                parts: uploadedParts,
                fileId,
                fileKey,
                processingJobId,
            },
        );

        return {
            ...completedMultipartsUploadResponse.data,
            fileId,
            fileKey,
            processingJobId,
        };
    } catch (error: any) {
        return rejectWithValue(error);
    }
});

export const uploadImportIndexFile = createAsyncThunk<
    { processingJobId: number },
    {
        data: ImportIndex;
        importType: TImportTargets;
    }
>("importData/uploadImportIndexFile", async ({ data, importType }, { rejectWithValue }) => {
    try {
        const response = await axios.put(
            import.meta.env.REACT_APP_API_ENDPOINT + "/history/imports/index-file",
            {
                data,
            },
            {
                params: { method: "create", importType },
            },
        );

        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error);
    }
});

export const getTable = createAsyncThunk(
    "importData/getTable",
    async (
        {
            jobId,
            filters,
        }: {
            jobId: number;
            filters?: {
                limit?: number;
                offset?: number;
                rows?: number[];
                correctionTableLinesOnly?: boolean;
            };
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/tables/${jobId}`,
                {
                    params: {
                        limit: filters?.limit,
                        offset: filters?.offset || 0,
                        rows: filters?.rows,
                        correctionTableLinesOnly: filters?.correctionTableLinesOnly,
                    },
                },
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

export const updateImportSalesRows = createAsyncThunk(
    "importData/updateSalesRows",
    async (
        {
            jobId,
            rows,
        }: {
            jobId: number;
            rows: {
                id: number | string;
                [key: string]: string | number | boolean;
            }[];
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/tables/${jobId}`,
                {
                    rows,
                },
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

export const deleteImportRowsWithErrors = createAsyncThunk(
    "importData/deleteImportRowsWithErrors",
    async (
        {
            jobId,
        }: {
            jobId: number;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/tables/${jobId}/errors`,
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

export const getImportTableErrorsCount = createAsyncThunk(
    "importData/getImportTableErrorsCount",
    async ({ jobId }: { jobId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/tables/${jobId}/errors-count`,
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

export const revalidateImportSalesTable = createAsyncThunk(
    "importData/revalidateImportSalesTable",
    async ({ jobId }: { jobId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/tables/${jobId}/revalidate`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

export const abortProcessingImport = createAsyncThunk(
    "importData/abortProcessingImport",
    async ({ jobId }: { jobId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/history/imports/tables/${jobId}`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

/**
 * Imports data asynchronously.
 */
export const uploadToBucket = createAsyncThunk(
    "importData/uploadToBucket",
    async (
        { JSONData, filename, URL }: { JSONData: TFileData; filename: string; URL: string },
        { rejectWithValue },
    ) => {
        try {
            const response = await fetch(URL, {
                method: "PUT",
                body: JSON.stringify({ data: JSONData, filename }),
            });

            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

/**
 * Downloads anomalies file for async import.
 */
export const downloadAnomaliesFile = createAsyncThunk(
    "importData/downloadAnomaliesFile",
    async (
        { excelData, fileName }: { excelData: { columns: any; data: any }; fileName: string },
        { rejectWithValue },
    ) => {
        try {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet("Anomalies");

            sheet.columns = excelData.columns;
            excelData.data.forEach((line) => {
                sheet.addRow(line);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: "application/octet-stream" });

            downloadFile({ source: blob, fileName: `${fileName}_anomalies.xlsx` });
            return { success: true };
        } catch (error) {
            return rejectWithValue(error);
        }
    },
);

export const bulkUpdateTermPeriodBalance = createAsyncThunk(
    "management/bulkUpdateTermPeriodBalance",
    async (data: { data: TObj[]; periodId: number; filename: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/management/balance/import", data),
                {
                    pending: { render: `${t("ImportData.Pending3")}`, closeOnClick: false, closeButton: false },
                    success: `${t("ImportData.SuccessToImportData")}`,
                    error: `${t("ImportData.FailedToImportData")}`,
                },
            );
            return response.data.result;
        } catch (err) {
            return rejectWithValue(getRejectPayload(err));
        }
    },
);

export type TImportDataSaleMetadata = {
    revenue?: number;
    invoiceable?: number;
    processingJobId: number | null;
    id: number;
    /** Channel selected at sales import. `null` means all channels. */
    channelId?: number | null;
};

type TImportDataSale = {
    id: number;
    user: string;
    date: string;
    target: string;
    filename: string;
    numberOfLines: "number";
    metadata: TImportDataSaleMetadata;
    processingJobId: string;
};

export type TImportError = {
    fileLine: number;
    entry: Record<string, string>;
    mappedKey: string;
    message: string;
    /** Rejected cell, when the API could name it. */
    value?: string;
    /** 1-based repeated column slot (channel block on a term row). */
    channelIndex?: number;
};

export interface IImportData {
    importDataStatus: TFetchStatus;
    importErrors: Record<string, any>[];
    importedSales: TImportDataSale[];
    importErrorMessage: string;
    importPayloadLimit: { payloadBytes: number; limitBytes: number } | null;
    bulkUpdateStatus: TFetchStatus;
    bulkUpdateErrors: TImportError[];
    exportURL: string;
    pendingImports: TPendingImport[];
}

const initialState: IImportData = {
    importDataStatus: "idle",
    importErrors: [],
    importedSales: [],
    importErrorMessage: "",
    importPayloadLimit: null,
    bulkUpdateStatus: "idle",
    bulkUpdateErrors: [],
    exportURL: "",
    pendingImports: [],
};

export const dataImportSlice = createSlice({
    name: "channels",
    initialState,
    reducers: {
        setImportDataStatus(state, action) {
            state.importDataStatus = action.payload;
        },
        clearErrors(state) {
            state.importErrors = [];
            state.importErrorMessage = "";
            state.importPayloadLimit = null;
        },
        resetImportModalState(state) {
            if (state.importDataStatus !== "pending") {
                state.importDataStatus = "idle";
            }
            state.importErrors = [];
            state.importErrorMessage = "";
            state.importPayloadLimit = null;
        },
        setPendingImports(state, action) {
            state.pendingImports = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchImportBeneficiars.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportBeneficiars.pending, (state) => {
            state.importDataStatus = "pending";
            state.importErrors = [];
        });
        builder.addCase(fetchImportBeneficiars.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.data.length}${t("ImportData.SuccessfullyImported")}`);
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });
        builder.addCase(bulkUpdates.rejected, (state, { payload }: Record<string, any>) => {
            state.bulkUpdateStatus = "failed";
            state.bulkUpdateErrors = getImportErrorsFromPayload(payload) as TImportError[];
        });
        builder.addCase(bulkUpdates.pending, (state) => {
            state.bulkUpdateStatus = "pending";
            state.bulkUpdateErrors = [];
        });
        builder.addCase(bulkUpdates.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.data.length}${t("ImportData.SuccessfullyImported")}`);
            state.bulkUpdateStatus = "succeeded";
            state.bulkUpdateErrors = [];
        });

        builder.addCase(fetchImportTitles.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportTitles.pending, (state) => {
            state.importDataStatus = "pending";
            state.importErrorMessage = "";
        });
        builder.addCase(fetchImportTitles.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.data.length}${t("ImportData.SuccessfullyImported")}`);
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });
        builder.addCase(verifyImportTitles.pending, (state) => {
            state.importDataStatus = "pending";
            state.importErrorMessage = "";
        });
        builder.addCase(verifyImportTitles.fulfilled, (state) => {
            state.importErrors = [];
        });
        builder.addCase(verifyImportTitles.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });

        builder.addCase(fetchImportArticles.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportArticles.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(fetchImportArticles.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.data.length}${t("ImportData.SuccessfullyImported")}`);
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });

        builder.addCase(fetchImportPublicationsWithArticles.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportPublicationsWithArticles.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(fetchImportPublicationsWithArticles.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.data.length}${t("ImportData.SuccessfullyImported")}`);
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });

        builder.addCase(fetchImportSales.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportSales.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(fetchImportSales.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.data.sales.length}${t("ImportData.SuccessfullyImported")}`);
            state.importDataStatus = "succeeded";
            state.importErrors = [];
            state.importedSales.push(action.payload);
        });

        builder.addCase(verifyImportTerms.pending, (state) => {
            state.importDataStatus = "pending";
            state.importErrorMessage = "";
        });
        builder.addCase(verifyImportTerms.fulfilled, (state) => {
            state.importErrors = [];
        });
        builder.addCase(verifyImportTerms.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportTerms.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportTerms.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(fetchImportTerms.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.data.length}${t("ImportData.SuccessfullyImported")}`);
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });

        builder.addCase(fetchImportStock.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportStock.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(fetchImportStock.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.stocks.length}${t("ImportData.SuccessfullyImported")}`);
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });

        builder.addCase(fetchImportProvision.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportProvision.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(fetchImportProvision.fulfilled, (state, action) => {
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });

        builder.addCase(fetchImportWorks.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportWorks.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(fetchImportWorks.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.data.length}${t("ImportData.SuccessfullyImported")}`);
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });
        builder.addCase(fetchImportAdvancePayments.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(fetchImportAdvancePayments.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(fetchImportAdvancePayments.fulfilled, (state, action) => {
            toast.success(`${action.meta.arg.data.length}${t("ImportData.SuccessfullyImported")}`);
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });
        builder.addCase(verifyImportSales.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(verifyImportSales.fulfilled, (state) => {
            state.importErrors = [];
        });
        builder.addCase(verifyImportSales.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(getSignedURL.fulfilled, (state, { payload }) => {
            state.exportURL = payload;
        });
        builder.addCase(getSignedURL.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(uploadToBucket.fulfilled, (state) => {
            state.bulkUpdateStatus = "succeeded";
        });
        builder.addCase(uploadToBucket.rejected, (state, { payload }) => {
            state.bulkUpdateStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(bulkUpdateTermPeriodBalance.rejected, (state, { payload }: Record<string, any>) => {
            applyFailedImportFromPayload(state, payload);
        });
        builder.addCase(bulkUpdateTermPeriodBalance.fulfilled, (state, action) => {
            state.importDataStatus = "succeeded";
            state.importErrors = [];
        });

        builder.addCase(multipartUpload.pending, (state) => {
            state.importDataStatus = "pending";
        });
        builder.addCase(multipartUpload.rejected, (state) => {
            state.importDataStatus = "failed";
        });
        // multipartUpload.fulfilled: keep pending — uploadImportIndexFile still runs (creates job)
        // Only set succeeded when job exists, else jobs fetch race re-enables button
        builder.addCase(uploadImportIndexFile.fulfilled, (state) => {
            state.importDataStatus = "succeeded";
        });
        builder.addCase(uploadImportIndexFile.rejected, (state) => {
            state.importDataStatus = "failed";
        });
    },
});

export const { setImportDataStatus, clearErrors, resetImportModalState, setPendingImports } = dataImportSlice.actions;

export default dataImportSlice.reducer;
